import { useMutation, useQuery } from "@apollo/client";
import {
  ArrowRightIcon,
  ChatAlt2Icon,
  CheckCircleIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  ShareIcon,
  UserCircleIcon,
  UsersIcon,
} from "@heroicons/react/outline";
import asanaLogo from "@static/img/asana.png";
import chatGptLogo from "@static/img/chatgpt-logo.svg";
import claudeLogo from "@static/img/claude-logo.svg";
import fellowLogo from "@static/img/fellow-logo.svg";
import firefliesLogo from "@static/img/fireflies-logo.png";
import geminiLogo from "@static/img/gemini-logo.svg";
import githubLogo from "@static/img/github.png";
import googleDriveLogo from "@static/img/google-drive.svg";
import granolaLogo from "@static/img/granola-logo.png";
import hubspotLogo from "@static/img/hubspot.png";
import jiraLogo from "@static/img/jira-icon.png";
import linearLogo from "@static/img/linear.png";
import notionLogo from "@static/img/notion.svg";
import pipedriveLogo from "@static/img/pipedrive-logo.svg";
import readAiLogo from "@static/img/read-ai-logo.svg";
import salesforceLogo from "@static/img/salesforce.png";
import slackLogo from "@static/img/slack.png";
import teamsLogo from "@static/img/teams.svg";
import youtrackLogo from "@static/img/youtrack.svg";
import { validate as validateEmail } from "email-validator";
import { compact } from "lodash";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { RelationshipType, UserStatus } from "types/graphql-schema";

import { errorNotificationVar } from "@cache/cache";
import usePusher from "@components/use-pusher/use-pusher";
import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

import getAccountSetupReportingQuery from "../graphql/get-account-setup-reporting-query";
import setAccountSetupRelationshipsMutation from "../graphql/set-account-setup-relationships-mutation";
import updateAccountOnboardingMutation from "../graphql/update-account-onboarding-mutation";

type RelationshipValue = "manager" | "report";

type TeammateRow = {
  key: string;
  userId?: number;
  firstName: string;
  lastName: string;
  email: string;
  relationship: RelationshipValue;
  userStatus?: UserStatus;
};

const roles = [
  "Founder / CEO",
  "Engineering",
  "Product",
  "People",
  "Customer",
  "Sales",
  "Marketing",
  "Operations",
  "Finance",
  "Data / Analytics",
  "IT",
  "Legal",
];

const integrations = [
  {
    id: "slack",
    name: "Slack",
    category: "Communication",
    icon: slackLogo,
  },
  {
    id: "teams",
    name: "Teams",
    category: "Communication",
    icon: teamsLogo,
  },
  {
    id: "granola",
    name: "Granola",
    category: "Notetaking",
    icon: granolaLogo,
  },
  {
    id: "fireflies",
    name: "Fireflies",
    category: "Notetaking",
    icon: firefliesLogo,
  },
  {
    id: "fellow",
    name: "Fellow",
    category: "Notetaking",
    icon: fellowLogo,
  },
  {
    id: "read_ai",
    name: "Read.AI",
    category: "Notetaking",
    icon: readAiLogo,
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    category: "AI",
    icon: chatGptLogo,
  },
  {
    id: "claude",
    name: "Claude",
    category: "AI",
    icon: claudeLogo,
  },
  {
    id: "gemini",
    name: "Gemini",
    category: "AI",
    icon: geminiLogo,
  },
  {
    id: "salesforce",
    name: "Salesforce",
    category: "CRM",
    icon: salesforceLogo,
  },
  {
    id: "hubspot",
    name: "HubSpot",
    category: "CRM",
    icon: hubspotLogo,
  },
  {
    id: "pipedrive",
    name: "Pipedrive",
    category: "CRM",
    icon: pipedriveLogo,
  },
  {
    id: "github",
    name: "Github",
    category: "Engineering",
    icon: githubLogo,
  },
  {
    id: "linear",
    name: "Linear",
    category: "Engineering",
    icon: linearLogo,
  },
  { id: "jira", name: "Jira", category: "Engineering", icon: jiraLogo },
  {
    id: "youtrack",
    name: "YouTrack",
    category: "Engineering",
    icon: youtrackLogo,
  },
  { id: "asana", name: "Asana", category: "Engineering", icon: asanaLogo },
  {
    id: "notion",
    name: "Notion",
    category: "Engineering",
    icon: notionLogo,
  },
  {
    id: "google_drive",
    name: "Google Drive",
    category: "Engineering",
    icon: googleDriveLogo,
  },
];

const steps = ["profile", "integrations", "teammates", "finish"] as const;
const REPORTING_RELATIONSHIP_SCAN_TIMEOUT_MS = 30000;

const parseOnboardingData = (value: unknown) => {
  if (!value) {
    return {};
  }
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (typeof value === "object") {
    return value as Record<string, any>;
  }
  return {};
};

const splitName = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const formatNamePart = (value: string) =>
  value
    .replace(/[^a-zA-Z]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const inferNameFromEmail = (email: string) => {
  const trimmedEmail = email.trim();
  if (!trimmedEmail.includes("@")) {
    return { firstName: "", lastName: "" };
  }

  const localPart = trimmedEmail.split("@")[0]?.split("+")[0] || "";
  const parts = localPart
    .split(/[._-]+/)
    .map(formatNamePart)
    .filter(Boolean);

  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const getEmailDomain = (email: string) =>
  email.trim().toLowerCase().split("@")[1] || "";

const withInferredTeammateRowNames = (row: TeammateRow) => {
  const inferredNames = inferNameFromEmail(row.email);
  return {
    ...row,
    firstName: row.firstName || inferredNames.firstName,
    lastName: row.lastName || inferredNames.lastName,
  };
};

const getUserNamesForRelationshipRow = (user: any) => {
  const emailNames = inferNameFromEmail(user.email || "");
  const nameLooksLikeEmail =
    typeof user.name === "string" && user.name.includes("@");
  const fullNameParts =
    user.name && !nameLooksLikeEmail
      ? splitName(user.name)
      : { firstName: "", lastName: "" };

  return {
    firstName:
      user.firstName || fullNameParts.firstName || emailNames.firstName,
    lastName: user.lastName || fullNameParts.lastName || emailNames.lastName,
  };
};

const getPrimaryOrganization = (currentUser: any) =>
  currentUser?.organizations?.edges
    ?.map((edge: any) => edge?.node)
    .find((organization: any) => organization && !organization.personal) ||
  currentUser?.organizations?.edges?.[0]?.node;

const StepHeader = ({
  icon,
  title,
  description,
  success = false,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  success?: boolean;
}) => (
  <div className="flex flex-col items-center gap-2 text-center">
    <div
      className={classNames(
        "flex h-10 w-10 items-center justify-center rounded-lg",
        success
          ? "bg-emerald-100 text-emerald-700"
          : "bg-gray-100 text-gray-900",
      )}
    >
      {icon}
    </div>
    <div className="flex max-w-[480px] flex-col gap-3">
      <h1 className="text-3xl font-semibold leading-9 text-gray-900">
        {title}
      </h1>
      <p className="text-xl leading-8 text-gray-600">{description}</p>
    </div>
  </div>
);

const ProgressBar = ({
  stepIndex,
  complete,
}: {
  stepIndex: number;
  complete: boolean;
}) => (
  <div className="fixed inset-x-0 bottom-0 flex h-1.5">
    {steps.map((step, index) => (
      <div
        key={step}
        className={classNames(
          "flex-1",
          complete
            ? "bg-emerald-200"
            : index <= stepIndex
              ? "bg-blue-300"
              : "bg-[#fcfcfc]",
        )}
      />
    ))}
  </div>
);

const StepButton = ({
  children,
  primary,
  disabled,
  onClick,
}: {
  children: ReactNode;
  primary?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={classNames(
      "rounded-md px-3 py-1.5 text-sm font-medium leading-5 disabled:cursor-not-allowed disabled:opacity-50",
      primary
        ? "bg-blue-500 text-white hover:bg-blue-600"
        : "bg-white text-gray-600 hover:bg-gray-50",
    )}
  >
    {children}
  </button>
);

const AccountSetupOnboarding = ({ currentUser }: { currentUser: any }) => {
  const onboardingData = useMemo(
    () => parseOnboardingData(currentUser?.onboardingData),
    [currentUser?.onboardingData],
  );
  const accountSetup = (onboardingData as any).account_setup || {};
  const organization = getPrimaryOrganization(currentUser);
  const organizationId = organization?.id ? Number(organization.id) : null;
  const allowedEmailDomains = useMemo(
    () =>
      (organization?.domains || [])
        .filter(
          (domain: unknown): domain is string =>
            typeof domain === "string" && Boolean(domain),
        )
        .map((domain: string) => domain.toLowerCase()),
    [organization?.domains],
  );

  const [stepIndex, setStepIndex] = useState(0);
  const [managesPeople, setManagesPeople] = useState(
    accountSetup.profile?.managesPeople ?? true,
  );
  const [role, setRole] = useState(accountSetup.profile?.role || "");
  const [integrationIds, setIntegrationIds] = useState<string[]>(
    accountSetup.integrationIds || [],
  );
  const [teammateRows, setTeammateRows] = useState<TeammateRow[]>(
    () => accountSetup.teammates?.map(withInferredTeammateRowNames) || [],
  );
  const [sendInvites, setSendInvites] = useState(
    accountSetup.sendInvites ?? false,
  );
  const [rowsInitialized, setRowsInitialized] = useState(
    Boolean(accountSetup.teammates?.length),
  );
  const [relationshipScanTimedOut, setRelationshipScanTimedOut] =
    useState(false);

  const {
    data: reportingData,
    loading: reportingLoading,
    refetch,
  } = useQuery(getAccountSetupReportingQuery, {
    variables: { organizationId },
    skip: !organizationId,
    fetchPolicy: "cache-and-network",
  });

  const [updateAccountOnboarding, { loading: savingOnboarding }] = useMutation(
    updateAccountOnboardingMutation,
  );
  const [setAccountSetupRelationships, { loading: savingRelationships }] =
    useMutation(setAccountSetupRelationshipsMutation);

  const handleReportingPredictionsUpdated = useCallback(
    (event: any) => {
      if (
        !organizationId ||
        Number(event?.organizationId) !== Number(organizationId) ||
        Number(event?.userId) !== Number(currentUser?.id)
      ) {
        return;
      }
      refetch();
    },
    [currentUser?.id, organizationId, refetch],
  );

  usePusher(
    currentUser?.pusherChannelName || "",
    "reporting_predictions_updated",
    handleReportingPredictionsUpdated,
  );

  const reportingPredictions = reportingData?.me?.reportingPredictions;
  const predictionStatus = reportingPredictions?.status || "not_started";
  const predictions = reportingPredictions?.predictions || [];
  const relationshipScanFailed = predictionStatus === "failed";
  const hasExistingReportingRelationships =
    Boolean(reportingData?.me?.managers?.edges?.length) ||
    Boolean(reportingData?.me?.directReports?.edges?.length);
  const scanningRelationships =
    reportingData?.me?.calendarIsSyncingForFirstTime ||
    reportingLoading ||
    ["not_started", "queued", "running"].includes(predictionStatus);
  const showRelationshipScanLoading =
    scanningRelationships && !relationshipScanTimedOut;
  const showRelationshipScanError =
    relationshipScanFailed || relationshipScanTimedOut;
  const showNoReportingRelationshipsFound =
    stepIndex === 2 &&
    rowsInitialized &&
    !teammateRows.length &&
    !hasExistingReportingRelationships &&
    !showRelationshipScanLoading &&
    !showRelationshipScanError;

  useEffect(() => {
    if (stepIndex !== 2 || !scanningRelationships) {
      setRelationshipScanTimedOut(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      setRelationshipScanTimedOut(true);
    }, REPORTING_RELATIONSHIP_SCAN_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [scanningRelationships, stepIndex]);

  useEffect(() => {
    if (rowsInitialized) {
      return;
    }
    const predictedRows = predictions.map((prediction: any) => {
      const user = prediction.otherUser;
      const names = getUserNamesForRelationshipRow(user);
      return {
        key: `prediction-${user.id}`,
        userId: Number(user.id),
        firstName: names.firstName,
        lastName: names.lastName,
        email: user.email || "",
        userStatus: user.status,
        relationship:
          prediction.predictedDirection === "subject_manages_other"
            ? "report"
            : "manager",
      };
    });
    const existingManagerRows =
      reportingData?.me?.managers?.edges?.map((edge: any) => {
        const user = edge?.node;
        if (!user) {
          return null;
        }
        const names = getUserNamesForRelationshipRow(user);
        return {
          key: `manager-${user.id}`,
          userId: Number(user.id),
          firstName: names.firstName,
          lastName: names.lastName,
          email: user.email || "",
          userStatus: user.status,
          relationship: "manager" as RelationshipValue,
        };
      }) || [];
    const existingReportRows =
      reportingData?.me?.directReports?.edges?.map((edge: any) => {
        const user = edge?.node;
        if (!user) {
          return null;
        }
        const names = getUserNamesForRelationshipRow(user);
        return {
          key: `report-${user.id}`,
          userId: Number(user.id),
          firstName: names.firstName,
          lastName: names.lastName,
          email: user.email || "",
          userStatus: user.status,
          relationship: "report" as RelationshipValue,
        };
      }) || [];
    const initialRows = predictedRows.length
      ? predictedRows
      : compact([...existingManagerRows, ...existingReportRows]);
    if (
      !initialRows.length &&
      scanningRelationships &&
      !relationshipScanTimedOut
    ) {
      return;
    }
    setTeammateRows(initialRows);
    setRowsInitialized(true);
  }, [
    predictions,
    relationshipScanTimedOut,
    reportingData,
    rowsInitialized,
    scanningRelationships,
  ]);

  const saveAccountSetup = async (data: Record<string, any>) => {
    await updateAccountOnboarding({
      variables: {
        accountSetupData: JSON.stringify(data),
      },
    });
  };

  const handleNextFromProfile = async () => {
    await saveAccountSetup({
      profile: { managesPeople, role: role || null },
      step: "profile",
    });
    setStepIndex(1);
  };

  const handleNextFromIntegrations = async () => {
    await saveAccountSetup({
      integrationIds,
      step: "integrations",
    });
    setStepIndex(2);
  };

  const addTeammateRow = () => {
    setTeammateRows((rows) => [
      ...rows,
      {
        key: `manual-${Date.now()}`,
        firstName: "",
        lastName: "",
        email: "",
        relationship: "report",
      },
    ]);
  };

  const updateTeammateRow = (key: string, update: Partial<TeammateRow>) => {
    setTeammateRows((rows) =>
      rows.map((row) => (row.key === key ? { ...row, ...update } : row)),
    );
  };

  const getRowsWithData = () =>
    teammateRows
      .map(withInferredTeammateRowNames)
      .filter(
        (row) => row.userId || row.email || row.firstName || row.lastName,
      );

  const validateTeammateRows = (rowsWithData: TeammateRow[]) => {
    const invalidEmailRow = rowsWithData.find(
      (row) => !row.userId && row.email && !validateEmail(row.email),
    );
    if (invalidEmailRow) {
      errorNotificationVar({
        title: "Enter a valid email address",
        timeout: 5000,
      });
      return false;
    }
    const invalidDomainRow = rowsWithData.find(
      (row) =>
        row.email &&
        allowedEmailDomains.length > 0 &&
        !allowedEmailDomains.includes(getEmailDomain(row.email)),
    );
    if (invalidDomainRow) {
      errorNotificationVar({
        title: `Use an email address from ${allowedEmailDomains.join(", ")}`,
        timeout: 5000,
      });
      return false;
    }
    return true;
  };

  const handleNextFromTeammates = async () => {
    const rowsWithData = getRowsWithData();
    if (!validateTeammateRows(rowsWithData)) {
      return;
    }

    await saveAccountSetup({
      teammates: rowsWithData,
      sendInvites,
      step: "teammates",
    });
    setStepIndex(3);
  };

  const saveTeammateRelationships = async (rowsWithData: TeammateRow[]) => {
    if (!organizationId || !currentUser?.id) {
      return true;
    }

    const relationships = compact(
      rowsWithData.map((row) => {
        if (!row.userId) {
          return null;
        }
        return {
          otherUserId: row.userId,
          relationshipType:
            row.relationship === "manager"
              ? RelationshipType.ManagedBy
              : RelationshipType.Manages,
          firstName: row.firstName || null,
          lastName: row.lastName || null,
        };
      }),
    );
    const invitations = compact(
      rowsWithData.map((row) => {
        if (!row.email || row.userStatus === UserStatus.Active) {
          return null;
        }
        return {
          inviteeEmail: row.email,
          inviteMessage: "",
          organizationId,
          role: "member",
          firstName: row.firstName || null,
          lastName: row.lastName || null,
          skipIfActive: true,
          sendEmail: sendInvites,
          relationships: [
            {
              otherUserId: Number(currentUser.id),
              relationshipType:
                row.relationship === "manager"
                  ? RelationshipType.Manages
                  : RelationshipType.ManagedBy,
            },
          ],
        };
      }),
    );

    const response = await setAccountSetupRelationships({
      variables: {
        organizationId,
        userId: Number(currentUser.id),
        relationships,
        invitations,
        saveRelationships:
          relationships.length > 0 || hasExistingReportingRelationships,
      },
    });
    const invitationError =
      response.data?.createInvitations?.invitationErrors?.[0]?.message;
    if (invitationError) {
      errorNotificationVar({ title: invitationError, timeout: 5000 });
      return false;
    }

    return true;
  };

  const handleFinish = async () => {
    const rowsWithData = getRowsWithData();
    if (!validateTeammateRows(rowsWithData)) {
      return;
    }
    const savedRelationships = await saveTeammateRelationships(rowsWithData);
    if (!savedRelationships) {
      return;
    }
    await saveAccountSetup({
      teammates: rowsWithData,
      sendInvites,
      step: "finish",
      completed: true,
    });
    window.location.href = "/finish-sign-up?next=/";
  };

  const toggleIntegration = (integrationId: string) => {
    setIntegrationIds((ids) =>
      ids.includes(integrationId)
        ? ids.filter((id) => id !== integrationId)
        : [...ids, integrationId],
    );
  };

  if (!currentUser) {
    return null;
  }

  const saving = savingOnboarding || savingRelationships;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-12 px-6 pb-10 pt-16">
      <div
        className={classNames(
          "flex w-full flex-col items-center justify-center gap-12",
          stepIndex === 2 || stepIndex === 3
            ? "max-w-[955px]"
            : "max-w-[501px]",
        )}
      >
        {stepIndex === 0 && (
          <div className="flex h-[570px] w-full flex-col items-center justify-center gap-8">
            <StepHeader
              icon={<UserCircleIcon className="h-6 w-6" />}
              title="Getting to know you"
              description="To better support you, can you please give us a few details about your role."
            />
            <div className="flex w-full flex-col gap-6 rounded-md border border-gray-200 bg-white p-6">
              <div className="grid grid-cols-[200px_1fr] items-start gap-4">
                <label className="pt-1.5 text-sm leading-5 text-gray-500">
                  Do you manage people?*
                </label>
                <div className="grid grid-cols-2 rounded-md border border-gray-200 bg-gray-100 p-0.5 text-sm font-medium">
                  <button
                    type="button"
                    onClick={() => setManagesPeople(true)}
                    className={classNames(
                      "rounded-md px-3 py-1.5",
                      managesPeople
                        ? "border border-gray-200 bg-white text-gray-800"
                        : "text-gray-500",
                    )}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setManagesPeople(false)}
                    className={classNames(
                      "rounded-md px-3 py-1.5",
                      !managesPeople
                        ? "border border-gray-200 bg-white text-gray-800"
                        : "text-gray-500",
                    )}
                  >
                    No
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-[200px_1fr] items-start gap-4">
                <label className="pt-1.5 text-sm leading-5 text-gray-500">
                  Your role (optional)
                </label>
                <select
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                  className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700"
                >
                  <option value="">Select a role</option>
                  {roles.map((roleOption) => (
                    <option key={roleOption} value={roleOption}>
                      {roleOption}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {stepIndex === 1 && (
          <div className="flex h-[570px] w-full max-w-[800px] flex-col items-center justify-center gap-8">
            <StepHeader
              icon={<ShareIcon className="h-6 w-6" />}
              title="What apps do you use most?"
              description="Our system will pull data from the apps you use regularly to provide insights inside Topicflow. You don't have to setup any integrations right now, but choosing these apps helps us understand your team better."
            />
            <div className="flex w-full min-w-[640px] flex-col gap-5">
              {["Communication", "Notetaking", "AI", "CRM", "Engineering"].map(
                (category) => (
                  <div
                    key={category}
                    className="grid grid-cols-[140px_1fr] gap-3"
                  >
                    <div className="flex h-10 items-center text-sm font-semibold text-gray-800">
                      {category}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {integrations
                        .filter(
                          (integration) => integration.category === category,
                        )
                        .map((integration) => {
                          const selected = integrationIds.includes(
                            integration.id,
                          );
                          return (
                            <button
                              type="button"
                              key={integration.id}
                              onClick={() => toggleIntegration(integration.id)}
                              className={classNames(
                                "flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium",
                                selected
                                  ? "border-blue-300 bg-blue-50 text-blue-700"
                                  : "border-gray-200 bg-white text-gray-800 hover:bg-gray-50",
                              )}
                            >
                              <span className="flex h-6 w-6 items-center justify-center">
                                <img
                                  src={getAssetUrl(integration.icon)}
                                  alt=""
                                  className="h-5 w-5 shrink-0 object-contain"
                                />
                              </span>
                              {integration.name}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        )}

        {stepIndex === 2 && (
          <div className="flex h-[570px] w-full flex-col items-center justify-center gap-8">
            <StepHeader
              icon={<UsersIcon className="h-6 w-6" />}
              title="Who are your teammates?"
              description="Our system will try to add reports and managers based on your calendar. Please add any that we've missed."
            />
            {showRelationshipScanLoading && (
              <div className="flex items-center gap-3 rounded-md bg-gray-100 p-3 text-sm font-medium text-gray-800">
                <RefreshIcon className="h-4 w-4 animate-spin" />
                We're scanning your calendar to find relationships...
              </div>
            )}
            {showRelationshipScanError && (
              <div className="rounded-md bg-amber-50 p-3 text-sm leading-5 text-amber-900">
                We couldn't automatically find your reporting relationships. You
                can still add managers and direct reports manually below.
              </div>
            )}
            {showNoReportingRelationshipsFound && (
              <div className="flex items-center gap-3 rounded-md bg-yellow-50 px-4 py-3 text-sm font-medium leading-5 text-gray-900">
                <SearchIcon className="h-4 w-4 shrink-0 text-gray-900" />
                Sorry, our system wasn't able to find any direct relationships.
              </div>
            )}
            <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
              <div className="grid grid-cols-[1fr_144px_144px_176px_32px] gap-3 border-b border-gray-200 px-6 py-4 text-xs font-medium text-gray-500">
                <div>Email address</div>
                <div>First name</div>
                <div>Last name</div>
                <div>Relationship</div>
                <div />
              </div>
              <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-6 py-4">
                {showRelationshipScanLoading && teammateRows.length === 0
                  ? [0, 1, 2, 3, 4].map((row) => (
                      <div
                        key={row}
                        className="flex h-8 items-center justify-center rounded-md bg-gray-50 text-gray-400"
                      >
                        <RefreshIcon className="h-4 w-4 animate-spin" />
                      </div>
                    ))
                  : teammateRows.map((row) => (
                      <div
                        key={row.key}
                        className="grid grid-cols-[1fr_144px_144px_176px_32px] gap-3"
                      >
                        <input
                          value={row.email}
                          onChange={(event) => {
                            const email = event.target.value;
                            const inferredNames = inferNameFromEmail(email);
                            updateTeammateRow(row.key, {
                              email,
                              firstName:
                                row.firstName || inferredNames.firstName,
                              lastName: row.lastName || inferredNames.lastName,
                            });
                          }}
                          className="h-8 rounded-md border border-gray-200 px-2 text-sm"
                        />
                        <input
                          value={row.firstName}
                          onChange={(event) =>
                            updateTeammateRow(row.key, {
                              firstName: event.target.value,
                            })
                          }
                          className="h-8 rounded-md border border-gray-200 px-2 text-sm"
                        />
                        <input
                          value={row.lastName}
                          onChange={(event) =>
                            updateTeammateRow(row.key, {
                              lastName: event.target.value,
                            })
                          }
                          className="h-8 rounded-md border border-gray-200 px-2 text-sm"
                        />
                        <select
                          value={row.relationship}
                          onChange={(event) =>
                            updateTeammateRow(row.key, {
                              relationship: event.target
                                .value as RelationshipValue,
                            })
                          }
                          className="h-8 rounded-md border border-gray-200 px-2 text-sm"
                        >
                          <option value="manager">Manager</option>
                          <option value="report">Direct report</option>
                        </select>
                        <button
                          type="button"
                          className="h-8 rounded-md text-sm text-gray-400 hover:bg-gray-50 hover:text-gray-700"
                          onClick={() =>
                            setTeammateRows((rows) =>
                              rows.filter(
                                (existingRow) => existingRow.key !== row.key,
                              ),
                            )
                          }
                        >
                          x
                        </button>
                      </div>
                    ))}
                <button
                  type="button"
                  onClick={addTeammateRow}
                  className="mt-auto flex w-full items-center justify-center gap-2 rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-100"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Person
                </button>
              </div>
            </div>
          </div>
        )}

        {stepIndex === 3 && (
          <div className="flex h-[570px] w-full flex-col items-center justify-center gap-8">
            <StepHeader
              success
              icon={<CheckCircleIcon className="h-6 w-6" />}
              title="You're all set!"
              description="Here are some recommended steps towards getting data and contextual information into the tool to best support you."
            />
            <div className="grid w-full grid-cols-3 gap-3">
              {[
                {
                  icon: <ChatAlt2Icon className="h-5 w-5 text-amber-500" />,
                  title: "Setup my first 1:1",
                  description:
                    "Start preparing for your next 1:1 by importing or adding your own notes.",
                },
                {
                  icon: <ShareIcon className="h-5 w-5 text-violet-500" />,
                  title: "Connect integrations",
                  description:
                    "Connect integrations to talk with your AI Agent, or start pulling data from your core work tools.",
                },
                {
                  icon: <UsersIcon className="h-5 w-5 text-blue-500" />,
                  title: "Tell us about your team",
                  description:
                    "Import notes from past 1:1s with your team and add other context for our system to learn about your team.",
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className="flex min-h-[122px] flex-col items-center gap-2 rounded-md border border-gray-200 bg-white p-6 text-center"
                >
                  {card.icon}
                  <div className="text-sm font-medium text-gray-800">
                    {card.title}
                  </div>
                  <div className="text-sm leading-5 text-gray-600">
                    {card.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex w-full items-center justify-between">
          {stepIndex > 0 && stepIndex < 3 ? (
            <StepButton onClick={() => setStepIndex(stepIndex - 1)}>
              Previous
            </StepButton>
          ) : (
            <div />
          )}
          {stepIndex === 0 && (
            <StepButton
              primary
              disabled={saving}
              onClick={handleNextFromProfile}
            >
              Next
            </StepButton>
          )}
          {stepIndex === 1 && (
            <StepButton
              primary
              disabled={saving}
              onClick={handleNextFromIntegrations}
            >
              Next
            </StepButton>
          )}
          {stepIndex === 2 && (
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm leading-5 text-gray-700">
                <input
                  type="checkbox"
                  checked={sendInvites}
                  onChange={(event) => setSendInvites(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-400 text-blue-500 focus:ring-blue-500"
                />
                Send invites to these people
              </label>
              <StepButton
                primary
                disabled={saving}
                onClick={handleNextFromTeammates}
              >
                Next
              </StepButton>
            </div>
          )}
          {stepIndex === 3 && (
            <button
              type="button"
              disabled={saving}
              onClick={handleFinish}
              className="mx-auto flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Take me to my account
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <ProgressBar stepIndex={stepIndex} complete={stepIndex === 3} />
    </div>
  );
};

export default AccountSetupOnboarding;
